import { Grid, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GridColumns } from "@mui/x-data-grid";
import React from "react";
import { useTranslation } from "react-i18next";

import { CompanyLegalEntityRef } from "../../models/company-accounts/company-legal-entities";
import { EmployeeGroupRef } from "../../models/employee-groups";
import { TnALocationCodeDTO } from "../../models/tna-location-codes/tna-location-code-dto";
import SvgFromString from "../../reusable-components/IconsSelect/SvgFromString/SvgFromString";
import SimplePopover from "../../reusable-components/SimplePopover/SimplePopover";
import Table from "../../reusable-components/Table/Table";
import { TnALocationCodesApiService } from "../../services/tna-location-codes/tna-location-codes-api.service";
import { FT_TLC } from "../../utils/constants/filter-tables-constants";
import { AddEditTnALocationCodes } from "../AddEditTnALocationCodes/AddEditTnALocationCodes";

const TnALocationCodesTable = () => {
  const { t } = useTranslation();
  const tRP = "components.tna_location_codes";
  const tRPFields = `${tRP}.fields`;

  const theme = useTheme();

  const columns: GridColumns<TnALocationCodeDTO> = [
    {
      field: "name",
      renderHeader: () => <Typography>{t("common_words.name")}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "code",
      renderHeader: () => <Typography>{t("common_words.code")}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "shortCode",
      renderHeader: () => <Typography>{t(`${tRPFields}.short_code`)}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0.7,
    },
    {
      field: "icon",
      headerName: t(`${tRPFields}.icon`),
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0.5,
      renderCell: ({ row }) => {
        return (
          <Grid sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {row?.icon && (
              <SvgFromString
                svgString={row.icon}
                color={theme.palette.secondary.light}
                width="25px"
              />
            )}
          </Grid>
        );
      },
    },
    {
      field: "employeeGroups",
      renderHeader: () => <Typography>{t(`${tRPFields}.employee_groups`)}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: false,
      valueGetter: ({ row }) => row.employeeGroups?.map((group: EmployeeGroupRef) => group?.name).join(", "),
      flex: 1.5,
    },
    {
      field: "legalEntities",
      renderHeader: () => <Typography>{t(`${tRPFields}.legal_entities`)}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: false,
      valueGetter: ({ row }) => row.legalEntities?.map((entity: CompanyLegalEntityRef) => entity?.name).join(", "),
      flex: 1.5,
    },
    {
      field: "keyForQRCode",
      renderHeader: () => <Typography>{t(`${tRPFields}.qr_code_key`)}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "usersAllowedToGenerateQRCode",
      renderHeader: () => <Typography>{t(`${tRPFields}.users_allowed_to_generate_qr_code`)}</Typography>,
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Grid style={{ display: "flex" }}>
          <Grid>
            {row.usersAllowedToGenerateQRCode.slice(0, 3).map((user, key) => (
              <Tooltip
                title={`${user.name} (${user.email})`}
                key={key}
              >
                <Typography variant="body2">{user.name}</Typography>
              </Tooltip>
            ))}
          </Grid>
          {row.usersAllowedToGenerateQRCode.length > 3 && (
            <SimplePopover
              buttonType="text"
              buttonContent={
                <Grid
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                  }}
                >
                  {`+ ${row.usersAllowedToGenerateQRCode.length - 3}`}
                </Grid>
              }
            >
              <Grid style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {row.usersAllowedToGenerateQRCode.slice(3).map((user) => (
                  <Tooltip
                    key={user.id}
                    title={`${user.name} (${user.email})`}
                  >
                    <Typography variant="body2">{user.name}</Typography>
                  </Tooltip>
                ))}
              </Grid>
            </SimplePopover>
          )}
        </Grid>
      ),
    },
  ];

  const defaultItem: Partial<TnALocationCodeDTO> = {
    name: "",
    code: "",
    shortCode: "",
    icon: "",
    employeeGroups: [],
    legalEntities: [],
    keyForQRCode: "",
    usersAllowedToGenerateQRCode: [],
  };

  return (
    <Table
      translationRootPath={tRP}
      columns={columns}
      serviceApi={new TnALocationCodesApiService()}
      defaultItem={defaultItem}
      filter={FT_TLC}
      addEditType="custom"
      CustomAddEditComponent={AddEditTnALocationCodes}
    />
  );
};

export default TnALocationCodesTable;
